import { useImageValidator } from '@/components/ui/custom/input/ImageUpload.hooks';
import { ChangeEvent, DragEvent, InputHTMLAttributes } from 'react';
import { twMerge } from 'tailwind-merge';

type FileInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'onChange'
> & {
  onChange?(files: FileList | null): void;
  limit?: number;
  multiple?: boolean;
  onDragging?(isDragging: boolean): void;
  testId?: string;
};

export default function FileInput({
  className,
  accept = '.png,.jpg,.jpeg,.pdf,.zip',
  limit = Infinity,
  onDragging,
  testId,
  onChange,
  multiple = false,
  ...rest
}: FileInputProps) {
  const { validate } = useImageValidator({
    accept,
    limit,
  });

  const inputProps: InputHTMLAttributes<HTMLInputElement> = {
    ...rest,
    type: 'file',
    accept,
    multiple,
  };

  const handleFileChange = (files: FileList | null) => {
    if (!files) return;
    if (!files.length) return;

    for (const file of files) {
      if (!validate(file)) return;
    }

    onChange?.(files);
  };

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    handleFileChange(files);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    onDragging?.(true);
  };

  const handleDragLeave = () => {
    onDragging?.(false);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    onDragging?.(false);

    const files = event.dataTransfer.files;

    handleFileChange(files);
  };

  return (
    <input
      {...inputProps}
      data-testid={testId}
      className={twMerge(
        'flex w-full grow cursor-pointer opacity-0',
        className,
      )}
      onChange={handleOnChange}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    />
  );
}
